import type { CSSInterpolation } from '@emotion/serialize';
import type { ColumnDef, Row, Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import _ from 'lodash';
import type { CSSProperties, MouseEvent, ReactNode, Ref } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { AnimateExpand } from '@adflow/frontend/ui-common';
import { theme } from '@adflow/frontend/ui-theme';
import {
	scrollIntoView,
	useClickIgnoreDrag,
} from '@adflow/frontend/util-common';
import type { DataTableItemType } from '@adflow/shared/util-column-templates';

import { ExtendedSearch } from './ExtendedSearch';
import { getFlexCellStyle, getFlexRowStyle } from './flex-layout';
import { getStickyCellProps } from './sticky-layout';
import { styles } from './styles';
import {
	ToggleRowBlockerContextProvider,
	useToggleRowContext,
} from './ToggleRowBlocker';

export const alternateBackground = { backgroundColor: '#edfdff' };

export type ExpandedRowRenderer<T extends object> = (
	data: T,
	toggleRowExpanded: () => void,
	shouldScroll: boolean
) => ReactNode;

export type DataTableFullOnRowClick<T> = (
	data: T,
	toggleRowExpanded?: () => void,
	event?: MouseEvent<HTMLDivElement>,
	ctrlClick?: boolean
) => void;

export interface RowRendererProps<T extends object> {
	itemType: DataTableItemType;
	row: Row<T>;
	table: Table<T>;
	coloredColumnIds: string[];

	isExpanded: boolean;
	toggleRowExpanded: () => void;
	/** Used for initial expand scroll to */
	isFirstExpanded: boolean;

	tableWidth: CSSProperties['maxWidth'];
	archived?: boolean;
	columnsTemplate: ColumnDef<T>[];

	/** This needs to be wrapped in useCallback */
	expandedRowRender?: ExpandedRowRenderer<T>;
	/** This needs to be wrapped in useCallback */
	onRowClick?: DataTableFullOnRowClick<T>;
	columnWidths: Record<string, number>;
	highlightStyle?: (area: string, disableShadows?: boolean) => CSSInterpolation;
	rowStyle?: CSSInterpolation | ((data: T) => CSSInterpolation);
	rowHeight?: number;
	growLastColumn?: boolean;
}

function RowRendererImpl<T extends object>(props: RowRendererProps<T>) {
	const {
		row,
		table,
		coloredColumnIds,
		onRowClick,
		expandedRowRender,
		tableWidth,
		archived,
		isExpanded,
		toggleRowExpanded,
		isFirstExpanded,
		highlightStyle,
		rowStyle,
		rowHeight,
		growLastColumn,
		itemType,
	} = props;

	const cells = row.getVisibleCells();

	const [rowClicked, setRowClicked] = useState(false);

	const { tryToggleRow, toggleRowContext } =
		useToggleRowContext(toggleRowExpanded);

	// Scroll to row if open on initial render
	const containerRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const scrollRef = containerRef.current;

		if (isExpanded && isFirstExpanded && scrollRef) {
			requestAnimationFrame(() => {
				scrollIntoView(scrollRef);
			});
		}
		// We only want to scroll to row on initial render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onRowClickProps = useClickIgnoreDrag<HTMLDivElement>(
		useCallback(
			(e) => {
				onRowClick?.(
					row.original,
					() => {
						const successful = tryToggleRow();
						if (successful) {
							if (isExpanded) {
								setTimeout(() => setRowClicked(false), 500);
							} else {
								setRowClicked(true);
							}
						}
					},
					e,
					e.ctrlKey || e.metaKey
				);
			},
			[isExpanded, onRowClick, row.original, tryToggleRow]
		)
	);

	const originalRow = useCallback(
		(isExpanded_: boolean) => {
			return (
				<div
					ref={containerRef}
					onTransitionEnd={() => {
						toggleRowContext.blocker.reset?.();
					}}
					key={row.id}
					role="row"
					style={getFlexRowStyle(table)}
					css={styles.tableRow}
					data-testid="data-table-row"
					data-row-id={row.id}
					data-archived={
						'archived' in row.original ? row.original.archived : undefined
					}
					{...onRowClickProps}
				>
					<div css={{ display: 'flex' }}>
						{cells.map((cell, i) => {
							const isLastCell = i === cells.length - 1;
							const flexCellStyle = getFlexCellStyle(
								cell,
								isLastCell,
								growLastColumn ?? true
							);
							const cellProps = _.merge(
								{ style: flexCellStyle },
								getStickyCellProps(cell, table)
							);

							return (
								<div
									key={cell.id}
									role="cell"
									{...cellProps}
									css={[
										styles.allCells,
										styles.normalCell,
										onRowClick ? { cursor: 'pointer' } : null,

										archived && styles.normalCellArchived,
										isExpanded_ && { backgroundColor: '#FFF8DD' },
										typeof cell.column.columnDef.meta?.cellStyle === 'function'
											? cell.column.columnDef.meta.cellStyle(cell)
											: cell.column.columnDef.meta?.cellStyle,
										coloredColumnIds.includes(cell.column.id) &&
											alternateBackground,
										highlightStyle?.(
											('interactionId' in row.original
												? String(row.original.interactionId)
												: undefined) ?? row.id,
											true
										),
										typeof rowStyle === 'function'
											? rowStyle(row.original)
											: rowStyle,
										rowHeight && { height: rowHeight },
									]}
									data-col-name={cell.column.columnDef.meta?.name}
								>
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</div>
							);
						})}
					</div>
					<div css={{ zIndex: 2 }}>
						<div
							css={{
								marginTop: -24,
								marginLeft: 4,
							}}
						>
							<ExtendedSearch
								type={itemType}
								data={row.original}
								cells={cells}
							/>
						</div>
					</div>
				</div>
			);
		},
		[
			archived,
			cells,
			coloredColumnIds,
			highlightStyle,
			onRowClick,
			onRowClickProps,
			row.id,
			row.original,
			rowHeight,
			rowStyle,
			table,
			toggleRowContext.blocker,
			itemType,
			growLastColumn,
		]
	);

	const expandedRow = useCallback(
		(ref: Ref<HTMLDivElement>) => {
			if (!expandedRowRender) return null;

			return (
				<div
					ref={ref}
					css={{
						borderBottomColor: theme.colors.input.border,
						borderBottomWidth: '1px',
						backgroundColor: theme.colors.background.form,
						position: 'sticky',
						left: '0px',

						maxWidth: tableWidth,
					}}
					data-testid="data-table-row"
					data-row-id={row.id}
					data-table-row-expanded
				>
					{expandedRowRender(row.original, tryToggleRow, !rowClicked)}
				</div>
			);
		},
		[
			expandedRowRender,
			row.id,
			row.original,
			rowClicked,
			tableWidth,
			tryToggleRow,
		]
	);

	return (
		<ToggleRowBlockerContextProvider value={toggleRowContext}>
			{originalRow(isExpanded)}
			<AnimateExpand isExpanded={isExpanded}>{expandedRow}</AnimateExpand>
		</ToggleRowBlockerContextProvider>
	);
}

export const RowRenderer = memo(RowRendererImpl, (prevProps, nextProps) => {
	const check = (prop: keyof RowRendererProps<object>) => {
		if (prop === 'row') {
			const prevRow = _.get(prevProps, prop);
			const nextRow = _.get(nextProps, prop);

			const rowsEqual = _.isEqual(prevRow.original, nextRow.original);
			const expandedEqual = prevRow.getIsExpanded() === nextRow.getIsExpanded();
			const cellsEqual =
				prevRow.getVisibleCells().length === nextRow.getVisibleCells().length;

			const prevRowStickyCount = prevRow.getLeftVisibleCells().length;
			const nextRowStickyCount = nextRow.getLeftVisibleCells().length;

			const stickyCellsEqual = prevRowStickyCount === nextRowStickyCount;

			return rowsEqual && cellsEqual && expandedEqual && stickyCellsEqual;
		}

		return _.isEqual(_.get(prevProps, prop), _.get(nextProps, prop));
	};

	const propsToCheck: (keyof RowRendererProps<object>)[] = [
		'row',
		'tableWidth',
		'onRowClick',
		'isExpanded',
		'expandedRowRender',
		'columnWidths',
		'highlightStyle',
		'columnsTemplate',
	];

	return propsToCheck.every(check);
}) as typeof RowRendererImpl;
